import { useState, useMemo, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../../../../components/ui/icon'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { Button } from '../../../../components/ui/button'
import { Card, CardContent } from '../../../../components/ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { apiClient } from '../../../../api/client'
import { apiErrorCode } from '../../../../lib/apiError'
import { registerPasskey, browserSupportsWebAuthn, fetchProviders, passkeyAddErrorKey } from '../../../../lib/passkeys'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { mdiClockOutline, mdiKeyOutline, mdiKeyVariant, mdiPlus, mdiTrashCanOutline } from '@mdi/js'

export const Route = createFileRoute('/$org/user/me/passkeys')({
    component: UserPasskeysPage,
})

interface PasskeyInfo {
    uid: string
    name: string
    aaguid_label?: string
    backup_state?: boolean
    created_at: string
    last_used_at?: string | null
}

function UserPasskeysPage() {
    const { org } = Route.useParams()
    const queryClient = useQueryClient()
    const { t } = useTranslation('auth')

    const [addError, setAddError] = useState<string | null>(null)
    const [adding, setAdding] = useState(false)
    const [deleteUid, setDeleteUid] = useState<string | null>(null)
    const [deleteError, setDeleteError] = useState<string | null>(null)

    const breadcrumbs = useMemo(() => [
        { title: 'Home', path: `/${org}` },
        { title: t('passkeys.title') },
    ], [org, t])
    useBreadcrumbs(breadcrumbs)

    const { data, isLoading } = useQuery({
        queryKey: ['passkeys'],
        queryFn: async () => {
            const res = await apiClient.GET('/api/v1/auth/passkeys')
            if (res.error) throw new Error('Failed to load passkeys')
            return (res.data as any)?.data as PasskeyInfo[]
        },
    })

    // A server without WebAuthn configured can't register a passkey: say so
    // up front rather than letting the button fail (#1705).
    const { data: providers } = useQuery({ queryKey: ['auth-providers'], queryFn: fetchProviders })
    const passkeysOff = providers?.passkeys_enabled === false

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['passkeys'] })
    }, [queryClient])

    const handleAdd = useCallback(async () => {
        setAddError(null)
        if (!browserSupportsWebAuthn()) {
            setAddError(t('passkeys.notSupported'))
            return
        }
        setAdding(true)
        try {
            await registerPasskey('')
            invalidate()
        } catch (err) {
            setAddError(t(passkeyAddErrorKey(apiErrorCode(err))))
        } finally {
            setAdding(false)
        }
    }, [invalidate, t])

    const deleteMutation = useMutation({
        mutationFn: async (uid: string) => {
            const res = await apiClient.DELETE('/api/v1/auth/passkeys/{uid}', {
                params: { path: { uid } },
            })
            if (res.error) {
                const code = (res.error as any)?.code
                throw new Error(code === 'PASSKEY_LAST_AUTH_METHOD'
                    ? t('passkeys.lastAuthMethod')
                    : code === 'mfa_verification_required'
                        ? t('mfa.errors.verification_required')
                        : code === 'mfa_last_factor'
                            ? t('mfa.errors.last_factor')
                            : 'delete failed')
            }
        },
        onSuccess: () => {
            setDeleteUid(null)
            setDeleteError(null)
            invalidate()
        },
        onError: (err: Error) => {
            setDeleteError(err.message)
        },
    })

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return null
        return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dateString))
    }

    const passkeys = data || []

    return (
        <div className="space-y-6" data-testid="user-passkeys-page">
            <div>
                <PageHeader
                    icon={SECTION_ICONS.passkeys}
                    title={t('passkeys.title')}
                    description={t('passkeys.description')}
                    actions={
                    <>
                    <Button onClick={handleAdd} disabled={adding || passkeysOff} data-testid="add-passkey-btn">
                        <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                        {adding ? t('passkeys.adding') : t('passkeys.add')}
                    </Button>
                    </>
                    }
                />
                {passkeysOff && (
                    <div className="mt-3 text-muted-foreground text-sm" data-testid="passkeys-not-configured">{t('passkeys.notConfigured')}</div>
                )}
                {addError && (
                    <div className="mt-3 text-destructive text-sm" data-testid="passkey-add-error">{addError}</div>
                )}
            </div>

            <div>
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                    </div>
                )}

                {!isLoading && passkeys.length === 0 && (
                    <EmptyState
                        variant="inline"
                        icon={mdiKeyOutline}
                        title={t('passkeys.empty')}
                        data-testid="passkeys-empty"
                    />
                )}

                {passkeys.length > 0 && (
                    <div className="grid gap-3" data-testid="passkeys-list">
                        {passkeys.map((pk) => {
                            const lastUsed = formatDate(pk.last_used_at)
                            return (
                                <Card key={pk.uid} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <AppIcon path={mdiKeyVariant} size="md" className="text-primary flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <h3 className="text-sm font-medium text-foreground truncate">
                                                        {pk.name || pk.aaguid_label || 'Passkey'}
                                                    </h3>
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                                                        <span>{t('passkeys.created', { when: formatDate(pk.created_at) })}</span>
                                                        <span className="flex items-center gap-1">
                                                            <AppIcon path={mdiClockOutline} size="xs" />
                                                            {lastUsed ? t('passkeys.lastUsed', { when: lastUsed }) : t('passkeys.neverUsed')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => { setDeleteError(null); setDeleteUid(pk.uid) }}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`delete-passkey-btn-${pk.uid}`}
                                            >
                                                <AppIcon path={mdiTrashCanOutline} size="sm" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                )}
            </div>

            <AlertDialog open={!!deleteUid} onOpenChange={(open) => { if (!open) { setDeleteUid(null); setDeleteError(null) } }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('passkeys.deleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('passkeys.deleteDescription')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    {deleteError && (
                        <div className="text-destructive text-sm" data-testid="passkey-delete-error">{deleteError}</div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); if (deleteUid) deleteMutation.mutate(deleteUid) }}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('passkeys.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
